import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-apollo';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import IconButton from '@material-ui/core/IconButton';
import Button from 'components/common/Button';
import Select from 'components/form/Select';
import VoiceChatIcon from '@material-ui/icons/VoiceChat';
import {
  ADD_STUDENT,
  GET_GROUPS,
  UPDATE_GROUP,
  REMOVE_STUDENT,
  DELETE_GROUP,
  GET_ADMIN_ID,
  REMOVE_USER
} from 'graphql/groups';
import { GET_ADMIN_STUDENTS } from 'graphql/admin/students';
import useSnack from '../../../hooks/useSnack';
import EditGroupModal from './EditGroupModal';
import ClearIcon from '@material-ui/icons/Clear';
import withConfirm from 'hooks/withConfirm';
import reject from 'lodash/reject';
import client from 'graphql/client';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';

const useStyles = makeStyles((theme) => ({
  root: {
    '& a': {
      color: theme.palette.primary.main,
    },
  },
  avatar: {
    width: 30,
    height: 30,
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.text.secondary,
  },
  userRow: {
    'marginBottom': theme.spacing(1),
    '& .MuiChip-root': {
      fontSize: 11,
      fontWeight: 'bold',
      height: 18,
    },
  },
  remove: {
    fontSize: '1rem',
  },
  delete: {
    '&:hover': {
      background: 'none',
    },
  },
}));

const UserRow = ({
  user,
  classes,
  badge,
  role,
  userid,
  handleRemoveStudent,
  id,
  isAdmin,
  ...props
}) => (
  <Box
    display="flex"
    alignItems="center"
    className={classes.userRow}
    {...props}
  >
    <Avatar className={classes.avatar} src={user.avatar}>
      {user.user_first_name}
    </Avatar>
    <Typography
      variant="body2"
      style={{ flex: 1 }}
      color="inherit"
      component={role === 'student' && Link}
      to={`/students/${userid}/profile`}
    >
      {user.user_first_name}
      {user.user_last_name}
    </Typography>
    {role === 'student' && isAdmin && (
      <IconButton onClick={() => handleRemoveStudent(id, userid)}>
        <ClearIcon
          className={classes.remove}
          fontSize="small"
          color="secondary"
        />
      </IconButton>
    )}
    {badge}
  </Box>
);

const GroupCard = ({ group, ...props }) => {
  const classes = useStyles();
  const user = useSelector((s) => s.user.user);
  const [showAdd, setShowAdd] = useState();
  const [selected, setSelected] = useState();
  const [isActive, setIsActive] = useState(false);
  const [showGroupModal, setGroupModal] = useState(false);
  const students = useQuery(GET_ADMIN_STUDENTS, {
    variables: { product: 'group' },
  });
  const [addStudent] = useMutation(ADD_STUDENT);
  const [updateGroup] = useMutation(UPDATE_GROUP);
  const [removeGroupStudent] = useMutation(REMOVE_STUDENT);
  const [deleteGroup] = useMutation(DELETE_GROUP);
  const [removeUser] = useMutation(REMOVE_USER);
  const popupState = usePopupState({ variant: 'popover', popupId: 'menu' });
  const { data: profile } = useQuery(GET_ADMIN_ID, {
    variables: { studentId: selected },
  });

  const snack = useSnack();
  const isAdmin = user.type === 'admin';

  const closeModal = () => setGroupModal(false);

  const handleRemoveStudent = (id, userid) => {
    return props.confirm(() => onRemove(id, userid));
  };

  const handleEditGroup = () => {
    setGroupModal(true);
    popupState.close();
  };

  const handleRemoveGroup = () => {
    popupState.close();
    const groupID = group.id;
    return props.confirm(() => onRemoveGroup(groupID), {
      title: `Are you sure you want to delete ${group.name}?`,
    });
  };

  const onRemoveGroup = (id) => {
    return deleteGroup({
      variables: { roomId: group.room_id, id },
      update: (proxy, mutationResult) => {
        const { groups } = proxy.readQuery({
          query: GET_GROUPS,
        });
        const removedId = mutationResult.data.delete_groups_by_pk.id;
        const updated_groups = reject(
          groups,
          (group) => group.id === removedId,
        );
        proxy.writeQuery({
          query: GET_GROUPS,
          data: {
            groups: updated_groups,
          },
        });
      },
    })
      .then(() => snack.success('Group Removed'))
      .catch((e) => snack.error('Something went wrong!'));
  };

  const onRemove = (id, userid) => {
    return removeGroupStudent({
      variables: {
        userId: userid,
        roomId: group.room_id,
        convoId: group.sms_conversation_id,
        id,
      },
      update: (proxy, mutationResult) => {
        const { groups } = proxy.readQuery({
          query: GET_GROUPS,
        });
        const removedId = mutationResult.data.delete_group_students_by_pk.id;
        group['users'] = reject(group.users, (user) => user.id === removedId);
        proxy.writeQuery({
          query: GET_GROUPS,
          data: {
            groups: groups.map((g) => (g.id === group.id ? group : g)),
          },
        });
        const data = proxy.readQuery({
          query: GET_ADMIN_STUDENTS,
          variables: { product: 'group' },
        });
        const addedStudent = data?.students.filter(
          (user) => user.user_id === userid,
        );
        addedStudent[0].group_student = null;
        proxy.writeQuery({
          query: GET_ADMIN_STUDENTS,
          variables: { product: 'group' },
          data: {
            students: data?.students,
          },
        });
      },
    })
      .then(() => snack.success('Student Removed'))
      .catch((e) => snack.error('Something went wrong!'));
  };

  const onAdd = async () => {
    const data = [];
    data.push({ room_id: group.room_id, user_id: selected });
    data.push({
      room_id: group.room_id,
      user_id: profile?.students[0]?.admin_id,
    });
    return addStudent({
      variables: {
        groupId: group.id,
        userId: selected,
        lcId: group.lc_id,
        hcId: group.hc_id,
        convoId: group.sms_conversation_id,
        data,
      },
      update: (proxy, mutationResult) => {
        const data = proxy.readQuery({
          query: GET_ADMIN_STUDENTS,
          variables: { product: 'group' },
        });
        const addedStudent = data?.students.filter(
          (user) => user.user_id === selected,
        );
        addedStudent[0].group_student = undefined;
        proxy.writeQuery({
          query: GET_ADMIN_STUDENTS,
          variables: { product: 'group' },
          data: {
            students: data?.students,
          },
        });
        const { groups } = proxy.readQuery({
          query: GET_GROUPS,
        });
        group.users.push(mutationResult.data.row);
        proxy.writeQuery({
          query: GET_GROUPS,
          data: {
            groups: groups.map((g) => (g.id === group.id ? group : g)),
          },
        });
      },
    }).then(
      () => {
        snack.success('Student added to group');
        setShowAdd(false);
        setSelected();
      },
      (e) => snack.error(e.message),
    );
  };

  const removeOldCoaches = async () => {
    const deleteData = []
    deleteData.push({ room_id: group.room_id, user_id: group.lc_id });
    deleteData.push({ room_id: group.room_id, user_id: group.hc_id });
    await Promise.all(
      deleteData.map((i) =>
        removeUser({
          variables: {
            roomId: i.room_id,
            userId: i.user_id
          }
        })
      ),
    ).catch((e) => {
      console.log(e);
      snack.error('Something went wrong!');
    });
  }
  
  const onEdit = async (values) => {
    const payload = {
      name: values.group_name,
      lc_id: values.lc_id,
      hc_id: values.hc_id,
      fc_id: values.fc_id
    };
    const chatRoomData = []
    chatRoomData.push({ room_id: group.room_id, user_id: values.lc_id });
    chatRoomData.push({ room_id: group.room_id, user_id: values.hc_id });
    await removeOldCoaches()
    return updateGroup({
      variables: {
        id: group.id,
        data: payload,
        upsertStudents: group.users.map((user) => {
          return {
            id: user.user.student_profiles[0]?.id,
            user_id: user.user_id,
            lc_id: values.lc_id,
            hc_id: values.hc_id,
            fa_id: values.fc_id
          };
        }),
        chatRoomData
      },
    })
      .then(() => {
        setGroupModal(false);
        snack.success('Group Info updated');
      })
      .catch((e) => {
        console.log(e);
        snack.error('Something went wrong!');
      });
  };

  return (
    <>
      <Card className={classes.root}>
        <CardHeader
          avatar={
            <Avatar>{group.name.substr(0, 2).toLocaleUpperCase()}</Avatar>
          }
          title={group.name}
          subheader={`${group.users.length} student(s)`}
          action={
            <>
              <IconButton component={Link} to={`/meeting/group/${group.id}`}>
                <VoiceChatIcon color="secondary" />
              </IconButton>
              {isAdmin && (
                <IconButton {...bindTrigger(popupState)}>
                  <MoreVertIcon />
                </IconButton>
              )}
            </>
          }
        />
        <CardContent>
          <Box>
            <UserRow
              user={group.lc}
              classes={classes}
              badge={<Chip color={'primary'} label="CC" size={'small'} />}
            />
            <UserRow
              user={group.hc}
              classes={classes}
              badge={<Chip color={'secondary'} label="HC" size={'small'} />}
            />
            {group.fc && <UserRow
              user={group.fc}
              classes={classes}
              badge={<Chip color={'secondary'} label="FC" size={'small'} />}
            />}
            {group.users.map((u) => (
              <UserRow
                key={u.user_id}
                id={u.id}
                user={u.user}
                role="student"
                classes={classes}
                userid={u.user_id}
                isAdmin={isAdmin}
                handleRemoveStudent={handleRemoveStudent}
                user={u.user}
                classes={classes}
                badge={<Chip label="STUDENT" size={'small'} />}
              />
            ))}
          </Box>
          {showAdd && (
            <Box display="flex">
              <Select
                transformValue
                noMargin
                isMulti={false}
                value={selected}
                style={{ marginRight: 10 }}
                onChange={setSelected}
                loading={students.loading}
                options={students.data?.students
                  .filter((f) => f.group_student === null)
                  .map((i) => ({
                    label: `${i.user_first_name} ${i.user_last_name}`,
                    value: i.user_id,
                  }))}
              />
              <Button color="primary" variant="contained" onClick={onAdd}>
                Add
              </Button>
            </Box>
          )}
        </CardContent>
        {isAdmin && (
          <CardActions disableSpacing>
            {!showAdd ? (
              <Button color="primary" onClick={setShowAdd}>
                Add Student
              </Button>
            ) : (
              <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            )}
          </CardActions>
        )}
      </Card>
      <Menu {...bindMenu(popupState)}>
        <MenuItem onClick={handleEditGroup}>Edit</MenuItem>
        <MenuItem onClick={handleRemoveGroup}>Delete</MenuItem>
      </Menu>
      {showGroupModal && (
        <EditGroupModal group={group} onClose={closeModal} onSubmit={onEdit} />
      )}
    </>
  );
};

export default withConfirm(GroupCard);
